import React, { useState, useMemo, useEffect, useRef } from "react";
import Pagination from "../../lib/components/Pagination/Pagination";
import DataTable from "../../lib/components/DataTable";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useTimezone } from "../../lib/context/TimezoneProvider";
import RefreshButton from "../../lib/components/RefreshButton";
import { fetchChargerEvents } from "./hooks/api";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import PropTypes from "prop-types";
import { getEventColumns } from "./constants";
import "./styles.scss";
import { Box } from "@mui/material";

export default function Events({ activePreset, onPresetChange, onCustomChange, onRefresh }) {
  const { serialNumber, dateRange, siteId } = useGlobalFilters();
  const { effectiveTimezone } = useTimezone();
  const [apiData, setApiData] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showLoader, setShowLoader] = useState(true);
  const lastRequestKeyRef = useRef("");

  const allEvents = useMemo(() => apiData?.events || [], [apiData]);
  const totalItems = apiData?.totalCount || allEvents.length || 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 0;
  const startIndex = page * rowsPerPage;
  const paginatedData = allEvents.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
  }, [serialNumber, siteId, dateRange]);

  useEffect(() => {
    if (serialNumber && siteId && dateRange?.[0] && dateRange?.[1]) {
      loadEvents(serialNumber, siteId, dateRange, page, rowsPerPage);
    } else {
      lastRequestKeyRef.current = "";
      setShowLoader(false);
      setApiData({});
    }
    // Reset dedup key on cleanup so re-mounts (e.g. StrictMode) re-fetch correctly
    return () => {
      lastRequestKeyRef.current = "";
    };
  }, [serialNumber, siteId, dateRange, page, rowsPerPage]);

  async function loadEvents(serialNumber, siteId, dateRange, page, rowsPerPage) {
    const pageSize = (page + 1) * rowsPerPage;
    const requestKey = JSON.stringify({
      serialNumber,
      siteId,
      startDate: new Date(dateRange[0]).toISOString(),
      endDate: new Date(dateRange[1]).toISOString(),
      pageSize,
    });

    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;
    setShowLoader(true);
    try {
      const res = await fetchChargerEvents(serialNumber, {
        siteId,
        startDate: new Date(dateRange[0]).toISOString(),
        endDate: new Date(dateRange[1]).toISOString(),
        pageSize,
      });
      setApiData({
        ...res,
        events: res?.events ? [...res.events] : [],
      });
    } catch (e) {
      lastRequestKeyRef.current = "";
      setApiData({});
    }
    setShowLoader(false);
  }

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const eventColumns = useMemo(() => getEventColumns(effectiveTimezone), [effectiveTimezone]);

  return (
    <div className="ocpp-page">
      <div className="ocpp-page__content">
        <div className="evs-ocpp-header">
          <DateRangeToolbar
            activePreset={activePreset}
            onPresetChange={onPresetChange}
            onCustomChange={onCustomChange}
            showTimezone
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <RefreshButton
              onClick={() => {
                if (onRefresh) {
                  onRefresh();
                } else if (serialNumber && siteId && dateRange?.[0] && dateRange?.[1]) {
                  loadEvents(serialNumber, siteId, dateRange, page, rowsPerPage);
                }
              }}
              tooltip="Refresh events"
            />
          </Box>
        </div>

        <DataTable
          columns={eventColumns}
          rows={paginatedData}
          isLoading={showLoader}
          onFilter={() => {}}
          noDataHeight="45vh"
        />
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalItems={totalItems}
          itemLabel="events"
        />
      </div>
    </div>
  );
}

Events.propTypes = {
  activePreset: PropTypes.string,
  onPresetChange: PropTypes.func,
  onCustomChange: PropTypes.func,
  onRefresh: PropTypes.func,
};
